/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import {
  Box,
  Breadcrumbs,
  Typography,
  FormControl,
  Select,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import React, { useState, useMemo, useCallback } from "react";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import api from "../../../api";
import authenticatedFetch from "../../../api/authenticatedFetch";
import { MenuHeading } from "../../../components/menu_heading";
import camelize from "../../../utils/camelize";
import { Loader } from "../../../components/loader";
import { IndexTable } from "../../../components/index_table";
import {
  StyledBreadCrumbContainer,
  StyledLink,
} from "../../user_management/styles";
import { useInfiniteQuery, useQuery, keepPreviousData } from "@tanstack/react-query";
import { FilterContainer } from "../../../components/filter_container";
import { viewTypographyStyles, selectBaseStyles, StyledSearchBar } from "../styles";
import { StyledInputLabel } from "../../network_guests/styles";
import DropdownSearchBar from "../../../components/dorpdown_searchbar";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { UpdateButton } from "../../../components/update_button";
import { ResetButton } from "../../../components/reset_button";
import { formatDate } from "../../../utils/formatDate";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { SelectedMenuItem } from "../../../components/selected_menu_item/index";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { buildQueryParams } from "../../../utils/buildQueryParams";
import { normalizeSearchTerm } from "../../../utils/normalizeSearchTerm";
import { OrderAppliedDiscountModal } from "../../../components/order_discount_modal";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { datePickerStyles } from "../styles";
import { StyledMenuItem } from "../../../components/styled_menu_item";
import { hasSingleItem } from "../../../utils/hasSingleItem";
import { getSingleItemValue } from "../../../utils/getSingleItemValue";
import { Query_Keys } from "../../../lib/queryKeys";
import { DiscountAnalyticsCards } from "../../../components/discount_analytics_cards";
import { RenderDiscountCardsList } from "../../../components/render_discount_cards_list";
import { OrderNumberCell } from "../../../components/order_number_cell";
import { PerPage } from "../../../constants/perPage";
import {
  getDropdownNextPageParam,
  hasDisplayableDropdownValue,
  mergeOptionsIfChanged,
  mergeUniqueByKey,
  normalizeEmployee,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useDebounce } from "../../../utils/useDebounce";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import {
  applyDateFilterChange,
  buildEmployeeDiscountQueryFilters,
  CUSTOM_DATE,
  DATE_FILTER_OPTIONS,
  getReportingToday,
  isCustomDateRangeInvalid,
} from "./employeeDiscountFilters";

const ALL_VALUE = "All";
const EMPTY_DROPDOWN_OPTIONS = [];
const defaultFilter = {
  restaurant_group_id: ALL_VALUE,
  restaurant_sub_group_id: ALL_VALUE,
  restaurant_ids: [ALL_VALUE],
  network_guest_ids: ALL_VALUE,
  dateFilter: ALL_VALUE,
  customDateFrom: null,
  customDateTo: null,
  order_status: ALL_VALUE,
};

const EmployeeDiscounts = () => {
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')
  const [search, setSearch] = useState("");
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [filters, setFilters] = useState(defaultFilter);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilter);
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState("");
  const [restaurantSubGroupSearch, setRestaurantSubGroupSearch] = useState("");
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([]);
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([]);
  const [restaurantSubGroupOptionsCache, setRestaurantSubGroupOptionsCache] = useState([]);
  const [employeeOptionsCache, setEmployeeOptionsCache] = useState([]);
  const [csvLoading, setCsvLoading] = useState(false);
  const showNotification = useNotificationContext();
  const [selectedOrder, setSelectedOrder] = useState("");
  const [orderOpen, setOrderOpen] = useState("");
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const normalizedSearchTerm = useMemo(() => normalizeSearchTerm(search), [search]);
  const debouncedSearchTerm = useDebounce(normalizedSearchTerm, 300);
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300);
  const debouncedRestaurantSubGroupSearch = useDebounce(restaurantSubGroupSearch.trim(), 300);
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300);
  const debouncedEmployeeSearch = useDebounce(employeeSearch.trim(), 300);

  const OrderStatus = ["Reconciled", "Not Reconciled"];

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get(
      "/v1/admin/dropdowns/restaurant_groups_list",
      {
        params: {
          page: pageParam,
          per_page: PerPage,
          search: debouncedRestaurantGroupSearch || undefined,
        },
      },
    );
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({ search: debouncedRestaurantGroupSearch }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchRestaurantSubGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get(
      "/v1/admin/dropdowns/restaurant_sub_groups_list",
      {
        params: {
          page: pageParam,
          per_page: PerPage,
          search: debouncedRestaurantSubGroupSearch || undefined,
          restaurant_group_id:
            filters?.restaurant_group_id === ALL_VALUE
              ? undefined
              : filters?.restaurant_group_id,
        },
      },
    );
    return response?.data || [];
  };

  const {
    data: restaurantSubGroups = EMPTY_DROPDOWN_OPTIONS,
    isFetching: restaurantSubGroupFetching,
    isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantSubGroupsPage,
    hasNextPage: hasNextRestaurantSubGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_sub_groups_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      search: debouncedRestaurantSubGroupSearch,
    }),
    queryFn: fetchRestaurantSubGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    enabled: filters?.restaurant_group_id !== ALL_VALUE,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    let res = await api.get("/v1/admin/dropdowns/restaurants_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
        restaurant_group_ids:
          filters?.restaurant_group_id === ALL_VALUE
            ? undefined
            : filters?.restaurant_group_id,
        restaurant_sub_group_id:
          filters?.restaurant_sub_group_id === ALL_VALUE
            ? undefined
            : filters?.restaurant_sub_group_id,
      },
    });
    return res?.data?.map(normalizeRestaurant) || [];
  };

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      restaurant_sub_group_id: filters?.restaurant_sub_group_id,
      search: debouncedRestaurantSearch,
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchEmployeeList = async ({ pageParam = 1 }) => {
    const response = await api.get(
      "/v1/admin/dropdowns/active_guests_list",
      {
        params: {
          page: pageParam,
          per_page: PerPage,
          search: debouncedEmployeeSearch || undefined,
          restaurant_id: filters?.restaurant_ids?.includes(ALL_VALUE) ? undefined : filters?.restaurant_ids,
          restaurant_group_id: filters?.restaurant_group_id === ALL_VALUE ? undefined : filters?.restaurant_group_id,
          restaurant_sub_group_id:
            filters?.restaurant_sub_group_id === ALL_VALUE ? undefined : filters?.restaurant_sub_group_id,
        },
      },
    );
    return response?.data
      ?.map(normalizeEmployee)
      ?.filter((employee) => (
        hasDisplayableDropdownValue(employee?.label)
        && hasDisplayableDropdownValue(employee?.value)
      )) || [];
  };

  const {
    data: employeeList = [],
    isFetching: employeeFetching,
    isFetchingNextPage: employeeListFetchingNextPage,
    fetchNextPage: fetchNextEmployeeListPage,
    hasNextPage: hasNextEmployeeListPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.active_guests_list({
      restaurant_group_id: filters?.restaurant_group_id,
      restaurant_sub_group_id: filters?.restaurant_sub_group_id,
      restaurant_id: filters?.restaurant_ids,
      search: debouncedEmployeeSearch,
    }),
    queryFn: fetchEmployeeList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  });

  const { rootRef: restaurantSubGroupMenuRef, targetRef: restaurantSubGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantSubGroupsPage,
    hasNextPage: hasNextRestaurantSubGroupsPage,
    isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
  });

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  });

  const { rootRef: employeeMenuRef, targetRef: employeeLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextEmployeeListPage,
    hasNextPage: hasNextEmployeeListPage,
    isFetchingNextPage: employeeListFetchingNextPage,
  });

  const fetchEmployeeDiscounts = async () => {
    const queryFilters = formattedFilters(appliedFilters, debouncedSearchTerm)
    const response = await api.get('/v1/admin/discounts', {
      params: {
        per_page: itemsPerPage,
        page: page,
        ...queryFilters,
        ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
      },
    });
    return {
      data: response?.data,
      pageCount: response?.headers["total-count"],
    };
  };

  const { data: discounts, isFetching } = useQuery({
    queryKey: Query_Keys.employee_discounts({
      filters: appliedFilters,
      itemsPerPage: itemsPerPage,
      page: page,
      sortBy,
      sortOrder,
      search: debouncedSearchTerm,
    }),
    queryFn: fetchEmployeeDiscounts,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchEmployeeDiscountAnalytics = async () => {
    const queryFilters = formattedFilters(appliedFilters, debouncedSearchTerm);
    const response = await api.get('/v1/admin/discounts/analytics', {
      params: queryFilters,
    });

    return response?.data;
  };

  const { data: discountAnalytics, isFetching: analyticsFetching } = useQuery({
    queryKey: Query_Keys.employee_discounts_analytics({
      filters: appliedFilters,
      search: debouncedSearchTerm,
    }),
    queryFn: fetchEmployeeDiscountAnalytics,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchOrderAppliedDiscount = async () => {
    if (!selectedOrder?.order_id) {
      return [];
    } else {
      const response = await api.get(
        `/v1/admin/orders/${selectedOrder?.order_id}/detail`,
      );
      return response?.data;
    }
  };

  const { data: orderData, isLoading: orderLoading } = useQuery({
    queryKey: Query_Keys.applied_discount({
      selected_order: selectedOrder?.order_id,
    }),
    queryFn: fetchOrderAppliedDiscount,
    staleTime: 5 * 60 * 1000,
    enabled: selectedOrder !== "",
  });

  React.useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurants, "id"));
  }, [restaurants]);

  React.useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurantGroups, "label"));
  }, [restaurantGroups]);

  React.useEffect(() => {
    setRestaurantSubGroupOptionsCache((currentOptions) => (
      mergeOptionsIfChanged(currentOptions, restaurantSubGroups, "value", ["label"])
    ));
  }, [restaurantSubGroups]);

  React.useEffect(() => {
    setEmployeeOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, employeeList, "value"));
  }, [employeeList]);

  const formattedFilters = (sourceFilters = filters, searchTerm = debouncedSearchTerm) => {
    return buildEmployeeDiscountQueryFilters(sourceFilters, { search: searchTerm });
  };

  const handleSearch = useCallback((searchQuery) => {
    setSearch(searchQuery);
    setPage(1);
  }, []);

  const getEmployeeName = (id) => {
    if (id === ALL_VALUE) return "All";
    const employee = employeeOptionsCache?.find((emp) => emp.value === id);
    return employee ? employee?.label : "";
  };

  const getRestaurantGroupName = (id) => {
    if (id === ALL_VALUE) return "All";
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => res.label === id);
    return restaurantGroup?.value ? restaurantGroup?.value : "";
  };

  const selectedRestaurantSubGroup = restaurantSubGroupOptionsCache?.find(
    (sub) => `${sub?.value}` === `${filters?.restaurant_sub_group_id}`,
  );

  // Keep the selected sub group in the list so the Select always has a matching
  // option, even when a search excludes it.
  const displayedRestaurantSubGroups = selectedRestaurantSubGroup
    ? mergeUniqueByKey(restaurantSubGroups, [selectedRestaurantSubGroup], "value")
    : restaurantSubGroups;

  const getRestaurantSubGroupName = (id) => {
    if (id === ALL_VALUE) return "All";
    const restaurantSubGroup = restaurantSubGroupOptionsCache?.find(
      (sub) => `${sub?.value}` === `${id}`,
    );
    return restaurantSubGroup?.label || "";
  };

  const handleDropDownChange = (event) => {
    const {
      target: { value },
    } = event;
    setFiltersChanged(true);

    if (
      (value?.length === 1 && value.includes(ALL_VALUE)) ||
      value?.length === 0
    ) {
      setFilters({ ...filters, restaurant_ids: [ALL_VALUE], network_guest_ids: ALL_VALUE });
    } else {
      const newRestaurantIds = value.filter((val) => val !== ALL_VALUE);
      setFilters({ ...filters, restaurant_ids: newRestaurantIds, network_guest_ids: ALL_VALUE });
    }
  };

  const handleRestaurantGroupChange = (event) => {
    // A new group invalidates the sub group list, so drop any pending sub group search.
    setRestaurantSubGroupSearch("");
    setRestaurantSearch("");
    setFilters({
      ...filters,
      restaurant_group_id: event.target.value,
      restaurant_sub_group_id: ALL_VALUE,
      restaurant_ids: [ALL_VALUE],
      network_guest_ids: ALL_VALUE,
    });
    setFiltersChanged(true);
  };

  const handleRestaurantGroupClear = () => {
    setRestaurantSubGroupSearch("");
    setRestaurantSearch("");
    setFilters({
      ...filters,
      restaurant_group_id: ALL_VALUE,
      restaurant_sub_group_id: ALL_VALUE,
      restaurant_ids: [ALL_VALUE],
      network_guest_ids: ALL_VALUE,
    });
    setFiltersChanged(true);
  };

  const handleRestaurantSubGroupChange = (event) => {
    setRestaurantSearch("");
    setFilters({
      ...filters,
      restaurant_sub_group_id: event.target.value,
      restaurant_ids: [ALL_VALUE],
      network_guest_ids: ALL_VALUE,
    });
    setFiltersChanged(true);
  };

  const handleRestaurantSubGroupClear = () => {
    setRestaurantSearch("");
    setFilters({
      ...filters,
      restaurant_sub_group_id: ALL_VALUE,
      restaurant_ids: [ALL_VALUE],
      network_guest_ids: ALL_VALUE,
    });
    setFiltersChanged(true);
  };

  const handleRestaurantClear = () => {
    setFilters({ ...filters, restaurant_ids: [ALL_VALUE], network_guest_ids: ALL_VALUE });
    setFiltersChanged(true);
  };

  const handleDateClear = () => {
    setFilters({ ...filters, dateFilter: ALL_VALUE, customDateFrom: null, customDateTo: null });
    setFiltersChanged(true);
  };

  const handleEmployeeChange = (event) => {
    setFilters({ ...filters, network_guest_ids: event.target.value });
    setFiltersChanged(true);
  };

  const handleDateChange = useCallback((field, value) => {
    setFilters((prev) => applyDateFilterChange(prev, field, value));
    setFiltersChanged(true);
  }, []);

  const handleOrderStatusChange = (event) => {
    setFilters({ ...filters, order_status: event.target.value });
    setFiltersChanged(true);
  };

  const handleOrderStatusClear = () => {
    setFilters({ ...filters, order_status: ALL_VALUE });
    setFiltersChanged(true);
  };

  const handleEmployeeClear = () => {
    setFilters({ ...filters, network_guest_ids: ALL_VALUE });
    setFiltersChanged(true);
  };

  const handleChange = () => {
    setAppliedFilters(filters);
    setFiltersChanged(false);
    setItemsPerPage(25);
    setPage(1);
  };

  const handleReset = () => {
    setFiltersChanged(false);
    setAppliedFilters(defaultFilter);
    setItemsPerPage(25);
    setPage(1);
    setSearch("");
    setFilters(defaultFilter);
  };

  const isUpdateDisabled = isCustomDateRangeInvalid(filters);

  const getRestaurantDisplayValue = () => {
    if (!filters?.restaurant_ids || filters?.restaurant_ids?.length === 0) {
      return "All";
    }

    if (filters?.restaurant_ids?.includes(ALL_VALUE)) {
      return "All";
    }

    if (filters?.restaurant_ids?.length === 1 && restaurantOptionsCache) {
      const restaurant = restaurantOptionsCache.find(
        (r) => r.id === filters.restaurant_ids[0],
      );
      return restaurant ? restaurant.name : "All";
    }

    return `${filters?.restaurant_ids?.length} Restaurants Selected`;
  };

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && hasSingleItem(restaurantGroups);
  const hasSingleAvailableRestaurant = !debouncedRestaurantSearch && hasSingleItem(restaurants);
  const hasSingleAvailableEmployee = !debouncedEmployeeSearch && hasSingleItem(employeeList);

  const handleOrderOpen = (order) => {
    setOrderOpen(true);
    setSelectedOrder(order);
  };

  const handleOrderClose = () => {
    setOrderOpen(false);
  };

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);
      const queryFilters = formattedFilters(appliedFilters, debouncedSearchTerm);
      const query = buildQueryParams(queryFilters);
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=employee_discount&${query}`;

      const { response } = await authenticatedFetch(url, {
        method: "GET",
        defaultErrorMessage: "Failed to download CSV",
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = downloadUrl;
      const searchSuffix = debouncedSearchTerm
        ? `_${debouncedSearchTerm
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "")
          .slice(0, 40)}`
        : "";
      const filename = `employee_discount${searchSuffix}_${getReportingToday().format("YYYY-MM-DD")}.csv`;

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification("CSV downloaded successfully", "success");
    } catch (error) {
      console.error("CSV download error:", error);
      showNotification(error?.message || "Failed to download CSV", "error");
    } finally {
      setCsvLoading(false);
    }
  };

  const totalAmount = (discount, amount) => {
    const discountTotal = Number(discount) ? Number(discount) : 0.0;
    const amountTotal = Number(amount) ? Number(amount) : 0.0;
    return Number(discountTotal + amountTotal).toFixed(2);
  };

  React.useEffect(() => {
    if (hasSingleAvailableRestaurantGroup) {
      const singleRestaurantGroup = getSingleItemValue(restaurantGroups);
      setFilters((prev) => ({
        ...prev,
        restaurant_group_id: singleRestaurantGroup.label,
      }));
    }
  }, [hasSingleAvailableRestaurantGroup, restaurantGroups]);

  React.useEffect(() => {
    if (hasSingleAvailableRestaurant) {
      const singleRestaurant = getSingleItemValue(restaurants);
      setFilters((prev) => ({
        ...prev,
        restaurant_ids: [singleRestaurant.id],
        network_guest_ids: ALL_VALUE,
      }));
    }
  }, [hasSingleAvailableRestaurant, restaurants]);

  React.useEffect(() => {
    if (hasSingleAvailableEmployee) {
      const singleEmployee = getSingleItemValue(employeeList);
      setFilters((prev) => ({
        ...prev,
        network_guest_ids: singleEmployee.value,
      }));
    }
  }, [employeeList, hasSingleAvailableEmployee]);

  const tableColumns = [
    { key: 'unique_id' },
    { key: 'employee_name' },
    { key: 'job_title', sortable: false },
    { key: 'check_in_date' },
    { key: 'order_number', label: 'Order #' },
    { key: 'order_details', label: 'Order Details', sortable: false },
    { key: 'order_date' },
    { key: 'visiting_restaurant' },
    { key: 'check_in_code' },
    { key: 'discount_card_used', label: 'Perq Card Used' },
    { key: 'total_order_amount_before_discount', label: 'Total Order Amount Before Perq' },
    { key: 'discount', label: 'Perq' },
    { key: 'total_order_amount_after_discount', label: 'Total Order Amount After Perq' },
  ]

  const columns = tableColumns?.map((column) => {
    return {
      key: column.key,
      label: column.label || camelize(column.key),
      sortable: column.sortable
    };
  });

  const TableRows = (data) => {
    return data?.map((record) => {
      return {
        unique_id: record?.network_guest?.phone_number || '-',
        employee_name: `${record?.network_guest?.first_name} ${record?.network_guest?.last_name}`,
        job_title: record?.network_guest?.job_title || '-',
        check_in_date: formatDate(record?.check_in, record?.time_zone_abbr),
        order_number: record?.order?.order_no,
        order_details: (
          <OrderNumberCell
            orderValue={record?.order}
            onView={handleOrderOpen}
          />
        ),
        order_date: formatDate(record?.order?.opened, record?.time_zone_abbr),
        visiting_restaurant: record?.visiting_restaurant?.name,
        check_in_code: record?.discount_code,
        discount_card_used: {
          display: (
            <RenderDiscountCardsList data={record?.discount_card ? [record?.discount_card] : []} />
          ),
          sortValue: record?.discount_card?.name,
        },
        total_order_amount_before_discount: `$${totalAmount(record?.total_discount, record?.order?.total)}`,
        discount: `$${Number(record?.total_discount).toFixed(2) ?? "0.00"}`,
        total_order_amount_after_discount: record?.order?.total
          ? `$${Number(record?.order?.total).toFixed(2) ?? "0.00"}`
          : "$0.00",
      };
    });
  };

  const rows = TableRows(discounts?.data);

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy)
    setSortOrder(nextSortOrder)
    setPage(1)
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          overflowY: "scroll",
          overflowX: "hidden",
        }}
      >
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
            <StyledLink color={"#515152"}>Reports</StyledLink>
            <StyledLink color={"#515152"}>Perq Reports</StyledLink>
            <StyledLink color={"#515152"}>Employee Perqs</StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>

        <FilterContainer>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: 2, md: 0 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: "18px" },
                flexWrap: "wrap",
                width: { xs: "100%", md: "auto" },
                flex: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  ...viewTypographyStyles,
                  alignSelf: { xs: "flex-start", sm: "center" },
                  whiteSpace: "nowrap",
                }}
              >
                View
              </Typography>

              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "160px" },
                }}
              >
                <StyledInputLabel>Restaurant Group</StyledInputLabel>
                <Select
                  size="small"
                  label="Restaurant Group"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: {
                      ref: restaurantGroupMenuRef,
                      sx: { maxHeight: "40vh" },
                    },
                    autoFocus: false,
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "160px" },
                    width: { xs: "100%", sm: "160px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleRestaurantGroupChange}
                  onClose={() =>
                    setTimeout(() => setRestaurantGroupSearch(""), 1000)
                  }
                  value={
                    hasSingleAvailableRestaurantGroup
                      ? getSingleItemValue(restaurantGroups)?.label
                      : filters?.restaurant_group_id || ALL_VALUE
                  }
                  renderValue={() => {
                    if (hasSingleAvailableRestaurantGroup) {
                      return (
                        getSingleItemValue(restaurantGroups)?.value || "All"
                      );
                    }
                    return getRestaurantGroupName(filters?.restaurant_group_id);
                  }}
                  endAdornment={
                    filters?.restaurant_group_id !== ALL_VALUE &&
                    !hasSingleAvailableRestaurantGroup ? (
                      <InputAdornment
                        position="end"
                        sx={{ position: "absolute", right: 25 }}
                      >
                        <IconButton
                          edge="end"
                          size="small"
                          disableRipple
                          onClick={handleRestaurantGroupClear}
                        >
                          <ClearIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                >
                  <DropdownSearchBar
                    changeSearchText={setRestaurantGroupSearch}
                    placeholder={"Search restaurant group"}
                    isFetching={restaurantGroupFetching}
                  />
                  {/* All option */}
                  {!hasSingleAvailableRestaurantGroup && (
                    <StyledMenuItem value={ALL_VALUE}>
                      {filters?.restaurant_group_id === ALL_VALUE ? (
                        <SelectedMenuItem data="All" />
                      ) : (
                        "All"
                      )}
                    </StyledMenuItem>
                  )}
                  {restaurantGroups
                    .map((res) => (
                      <StyledMenuItem key={res?.label} value={res?.label}>
                        {hasSingleAvailableRestaurantGroup ||
                        filters?.restaurant_group_id === res?.label ? (
                          <SelectedMenuItem data={res?.value} />
                        ) : (
                          res?.value
                        )}
                      </StyledMenuItem>
                    ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantGroupsPage}
                    targetRef={restaurantGroupLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl
                size="small"
                disabled={filters?.restaurant_group_id === ALL_VALUE}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "180px" },
                }}
              >
                <StyledInputLabel>Restaurant Sub Group</StyledInputLabel>
                <Select
                  size="small"
                  label="Restaurant Sub Group"
                  data-testid="employee-discounts-restaurant-sub-group-filter"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: {
                      ref: restaurantSubGroupMenuRef,
                      sx: { maxHeight: "40vh" },
                    },
                    autoFocus: false,
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "180px" },
                    width: { xs: "100%", sm: "180px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleRestaurantSubGroupChange}
                  onClose={() =>
                    setTimeout(() => setRestaurantSubGroupSearch(""), 1000)
                  }
                  value={filters?.restaurant_sub_group_id || ALL_VALUE}
                  renderValue={() =>
                    getRestaurantSubGroupName(filters?.restaurant_sub_group_id)
                  }
                  endAdornment={
                    filters?.restaurant_sub_group_id !== ALL_VALUE ? (
                      <InputAdornment
                        position="end"
                        sx={{ position: "absolute", right: 25 }}
                      >
                        <IconButton
                          edge="end"
                          size="small"
                          disableRipple
                          onClick={handleRestaurantSubGroupClear}
                        >
                          <ClearIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                >
                  <DropdownSearchBar
                    changeSearchText={setRestaurantSubGroupSearch}
                    placeholder={"Search restaurant sub group"}
                    isFetching={restaurantSubGroupFetching}
                  />
                  {/* All option */}
                  <StyledMenuItem value={ALL_VALUE}>
                    {filters?.restaurant_sub_group_id === ALL_VALUE ? (
                      <SelectedMenuItem data="All" />
                    ) : (
                      "All"
                    )}
                  </StyledMenuItem>
                  {displayedRestaurantSubGroups
                    .map((subGroup) => (
                      <StyledMenuItem key={subGroup?.value} value={subGroup?.value}>
                        {`${filters?.restaurant_sub_group_id}` === `${subGroup?.value}` ? (
                          <SelectedMenuItem data={subGroup?.label} />
                        ) : (
                          subGroup?.label
                        )}
                      </StyledMenuItem>
                    ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantSubGroupsPage}
                    targetRef={restaurantSubGroupLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "200px" },
                }}
              >
                <StyledInputLabel>Visiting Restaurant</StyledInputLabel>
                <Select
                  size="small"
                  label="Visiting Restaurant"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: {
                      ref: restaurantMenuRef,
                      sx: { maxHeight: "40vh" },
                    },
                    autoFocus: false,
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "200px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleDropDownChange}
                  onClose={() =>
                    setTimeout(() => setRestaurantSearch(""), 1000)
                  }
                  value={
                    hasSingleAvailableRestaurant
                      ? [getSingleItemValue(restaurants)?.id]
                      : filters?.restaurant_ids || [ALL_VALUE]
                  }
                  multiple
                  renderValue={() => {
                    if (hasSingleAvailableRestaurant) {
                      return getSingleItemValue(restaurants)?.name || "All";
                    }
                    return getRestaurantDisplayValue();
                  }}
                  endAdornment={
                    !filters?.restaurant_ids?.includes(ALL_VALUE) &&
                    !hasSingleAvailableRestaurant ? (
                      <InputAdornment
                        position="end"
                        sx={{ position: "absolute", right: 25 }}
                      >
                        <IconButton
                          edge="end"
                          size="small"
                          disableRipple
                          onClick={handleRestaurantClear}
                        >
                          <ClearIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                >
                  <DropdownSearchBar
                    changeSearchText={setRestaurantSearch}
                    placeholder={"Search visiting restaurant"}
                    isFetching={restaurantFetching}
                  />
                  {/* All option */}
                  {!hasSingleAvailableRestaurant && (
                    <StyledMenuItem
                      key={ALL_VALUE}
                      value={ALL_VALUE}
                      disabled={!filters?.restaurant_ids?.includes(ALL_VALUE)}
                    >
                      {filters?.restaurant_ids?.includes(ALL_VALUE) ? (
                        <SelectedMenuItem data="All" />
                      ) : (
                        "All"
                      )}
                    </StyledMenuItem>
                  )}
                  {restaurants
                    .map((res) => (
                      <StyledMenuItem key={res?.id} value={res?.id}>
                        {hasSingleAvailableRestaurant ||
                        filters?.restaurant_ids?.includes(res?.id) ? (
                          <SelectedMenuItem data={res?.name} />
                        ) : (
                          res?.name
                        )}
                      </StyledMenuItem>
                    ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantsPage}
                    targetRef={restaurantLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "200px" },
                }}
              >
                <StyledInputLabel>Employees</StyledInputLabel>
                <Select
                  size="small"
                  label="Employees"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: {
                      ref: employeeMenuRef,
                      sx: { maxHeight: "40vh" },
                    },
                    autoFocus: false,
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "200px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleEmployeeChange}
                  onClose={() => setTimeout(() => setEmployeeSearch(""), 1000)}
                  value={
                    hasSingleAvailableEmployee
                      ? getSingleItemValue(employeeList)?.value
                      : filters?.network_guest_ids || ALL_VALUE
                  }
                  renderValue={() => {
                    if (hasSingleAvailableEmployee) {
                      return getSingleItemValue(employeeList)?.label || "All";
                    }
                    return getEmployeeName(filters?.network_guest_ids);
                  }}
                  endAdornment={
                    filters?.network_guest_ids !== ALL_VALUE &&
                    !hasSingleAvailableEmployee ? (
                      <InputAdornment
                        position="end"
                        sx={{ position: "absolute", right: 25 }}
                      >
                        <IconButton
                          edge="end"
                          size="small"
                          disableRipple
                          onClick={handleEmployeeClear}
                        >
                          <ClearIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                >
                  <DropdownSearchBar
                    changeSearchText={setEmployeeSearch}
                    placeholder={"Search employee"}
                    isFetching={employeeFetching}
                  />
                  {/* All option */}
                  {!hasSingleAvailableEmployee && (
                    <StyledMenuItem value={ALL_VALUE}>
                      {filters?.network_guest_ids === ALL_VALUE ? (
                        <SelectedMenuItem data="All" />
                      ) : (
                        "All"
                      )}
                    </StyledMenuItem>
                  )}
                  {employeeList
                    .map((res) => (
                      <StyledMenuItem key={res?.value} value={res?.value}>
                        {hasSingleAvailableEmployee ||
                        res?.value === filters?.network_guest_ids ? (
                          <SelectedMenuItem data={res?.label} />
                        ) : (
                          res?.label
                        )}
                      </StyledMenuItem>
                    ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextEmployeeListPage}
                    targetRef={employeeLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "160px" },
                }}
              >
                <StyledInputLabel>Date Range</StyledInputLabel>
                <Select
                  value={filters?.dateFilter || ALL_VALUE}
                  renderValue={() => filters?.dateFilter}
                  label="Date Range"
                  size="small"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { sx: { width: "230px" } },
                    autoFocus: false,
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "160px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={(e) =>
                    handleDateChange("dateFilter", e.target.value)
                  }
                  endAdornment={
                    filters?.dateFilter !== ALL_VALUE ? (
                      <InputAdornment
                        position="end"
                        sx={{ position: "absolute", right: 25 }}
                      >
                        <IconButton
                          edge="end"
                          size="small"
                          disableRipple
                          onClick={handleDateClear}
                        >
                          <ClearIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                >
                  {/* All option */}
                  <StyledMenuItem value={ALL_VALUE}>
                    {filters?.dateFilter === ALL_VALUE ? (
                      <SelectedMenuItem data="All" />
                    ) : (
                      "All"
                    )}
                  </StyledMenuItem>
                  {DATE_FILTER_OPTIONS.map((value) => (
                    <StyledMenuItem value={value} key={value}>
                      {filters?.dateFilter === value ? (
                        <SelectedMenuItem data={value} />
                      ) : (
                        <>{value}</>
                      )}
                    </StyledMenuItem>
                  ))}
                </Select>
              </FormControl>
              {filters?.dateFilter === CUSTOM_DATE && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Typography variant="body2" sx={viewTypographyStyles}>
                      Days
                    </Typography>
                    <DatePicker
                      format="MM-DD-YYYY"
                      sx={{
                        ...datePickerStyles,
                        width: { xs: "100%", sm: "140px", lg: "160px" },
                      }}
                      slotProps={{ textField: { size: "small" } }}
                      value={filters.customDateFrom || null}
                      onChange={(date) =>
                        handleDateChange("customDateFrom", date)
                      }
                    />
                    <Typography variant="body2" sx={viewTypographyStyles}>
                      through
                    </Typography>
                    <DatePicker
                      sx={{
                        ...datePickerStyles,
                        width: { xs: "100%", sm: "140px", lg: "160px" },
                      }}
                      format="MM-DD-YYYY"
                      slotProps={{ textField: { size: "small" } }}
                      value={filters.customDateTo || null}
                      onChange={(date) =>
                        handleDateChange("customDateTo", date)
                      }
                    />
                  </LocalizationProvider>
                </Box>
              )}

              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "170px" },
                }}
              >
                <StyledInputLabel>Order Status</StyledInputLabel>
                <Select
                  size="small"
                  label="Order Status"
                  data-testid="employee-discounts-order-status-filter"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { sx: { maxHeight: "40vh", width: "220px" } },
                    autoFocus: false,
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "170px" },
                    borderColor: "#2b4fb9",
                  }}
                  value={filters?.order_status || ALL_VALUE}
                  onChange={handleOrderStatusChange}
                  renderValue={() => filters?.order_status}
                  endAdornment={
                    filters?.order_status !== ALL_VALUE ? (
                      <InputAdornment
                        position="end"
                        sx={{ position: "absolute", right: 25 }}
                      >
                        <IconButton
                          edge="end"
                          size="small"
                          disableRipple
                          onClick={handleOrderStatusClear}
                        >
                          <ClearIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                >
                  {/* All option */}
                  <StyledMenuItem value={ALL_VALUE}>
                    {filters?.order_status === ALL_VALUE ? (
                      <SelectedMenuItem data="All" />
                    ) : (
                      "All"
                    )}
                  </StyledMenuItem>
                  {OrderStatus?.map((res, index) => (
                    <StyledMenuItem key={index} value={res}>
                      {filters?.order_status === res ? (
                        <SelectedMenuItem data={res} />
                      ) : (
                        res
                      )}
                    </StyledMenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                alignSelf: { xs: "flex-start", md: "center" },
                mt: { xs: 1, md: 0 },
                width: { xs: "100%", md: "auto" },
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <ResetButton
                onClick={handleReset}
                filtersChanged={filtersChanged}
                status={isFetching}
              />
              <UpdateButton
                onClick={handleChange}
                filtersChanged={filtersChanged}
                status={isFetching}
                disabled={isUpdateDisabled}
              />
            </Box>
          </Box>
        </FilterContainer>

          <MenuHeading
            report={false}
            dividerStyle={{ marginBottom: '16px' }}
            rightComponent={
              <StyledSearchBar
                value={search}
                placeholder="Search employee perqs"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => handleSearch(e.target.value)}
                autoComplete="off"
                inputProps={{ 'data-testid': 'employee-perqs-search-input' }}
              />
            }
          >
            Employee Perqs Report
          </MenuHeading>
        
        <DiscountAnalyticsCards
          isMobile={isMobile}
          data={discountAnalytics}
          loading={analyticsFetching}
        />

        {(isFetching && !debouncedSearchTerm) ? (
          <Loader />
        ) : (
          discounts?.data && (
            <IndexTable
              columns={columns}
              data={rows}
              tableId="employee-discount-report-entries"
              type="entries"
              sortMode="server"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              page={page}
              setPage={setPage}
              pageCount={discounts?.pageCount}
              report={false}
              showDownloadButton={true}
              disableDownloadButton={discounts?.data.length === 0 || csvLoading}
              handleExportCsv={handleExportCsv}
              csvLoading={csvLoading}
              handleOpen={handleOrderOpen}
              emptyStateText="No Data Exists"
              hasSearchTerm={Boolean(debouncedSearchTerm)}
              searchLoading={isFetching && !!debouncedSearchTerm}
            />
          )
        )}
      </Box>

      {/* Order Detail Modal */}
      <OrderAppliedDiscountModal
        open={orderOpen}
        handleClose={handleOrderClose}
        ordersData={orderData}
        isLoading={orderLoading}
      />
    </React.Fragment>
  );
};

export default EmployeeDiscounts;
